import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ClipLoader from 'react-spinners/ClipLoader';
import { faAsterisk } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Col, DatePicker, Form, Input, Row, Select, message } from 'antd';
import cx from 'classnames';
import { observer } from 'mobx-react';
import moment from 'moment';
import Router from 'next/router';
import { getProjectOptions, listAdmins } from '@gtintel/platform-api';
import { QARequiredCheckbox, RequestAnonymityCheckbox } from '@gtintel/platform-atoms';
import { DEFAULT_DATE_PICKER_TIME_FORMAT, DEFAULT_TIME_FORMAT } from '@gtintel/platform-constants';
import { TagsForm } from '@gtintel/platform-organisms';
import { useStores } from '@gtintel/platform-stores';
import ProposalFields from '../ProposalFields';
import ProjectTypeSelect from './components/ProjectTypeSelect';
import * as S from './styles';
import styles from './styles.module.scss';
import { ValidateError } from './types';
const EditProjectDetailForm = ({
  onCancel
}) => {
  const {
    adminProjectDetailStore
  } = useStores();
  const tagsRef = useRef(null);
  const proposalFieldsRef = useRef(null);
  const {
    handleSubmit,
    control,
    watch,
    formState: {
      errors
    }
  } = useForm();
  const {
    projectDetail,
    projectTypes,
    isLoading
  } = adminProjectDetailStore;
  const datePickerRequest = moment(projectDetail.requested_completion_date, DEFAULT_DATE_PICKER_TIME_FORMAT).format(DEFAULT_DATE_PICKER_TIME_FORMAT);
  const [adminOpts, setAdminOpts] = useState([]);
  const [parentOpts, setParentOpts] = useState(null);
  const [isSubproject, setIsSubProject] = useState(!!projectDetail.parent_id);
  const [isExcludedFromStats, setIsExcludedFromStats] = useState(projectDetail.flags?.is_excluded_from_stats);
  const [isExcludedFromAdminDashboard, setIsExcludedFromAdminDashboard] = useState(projectDetail.flags?.is_excluded_from_admin_dashboard);
  const [isExcludedFromBilling, setIsExcludedFromBilling] = useState(projectDetail.flags?.is_excluded_from_billing);
  const [isExcludedFromSlackNotifications, setIsExcludedFromSlackNotifications] = useState(projectDetail.flags?.is_excluded_from_slack_notifications);
  const [canBeCharged, setCanBeCharged] = useState(projectDetail.flags?.can_be_charged);
  const [canBeBilled, setCanBeBilled] = useState(projectDetail.flags?.can_be_billed);
  const [canBeMatched, setCanBeMatched] = useState(projectDetail.flags?.can_be_matched);
  useEffect(() => {
    listAdmins().then(({
      results
    }) => {
      const opts = results.filter(admin => admin.is_active).map(admin => ({
        label: admin.full_name,
        value: admin.id
      }));
      setAdminOpts(opts);
    }).catch(() => {});

    // A project can't be a subproject of itself:
    getProjectOptions({
      exclude: projectDetail.id
    }).then(setParentOpts).catch(() => {});
  }, []);
  const datePickerQaDueDate = (() => {
    if (projectDetail.np_completion_date) {
      return moment(projectDetail.np_completion_date, DEFAULT_DATE_PICKER_TIME_FORMAT).format(DEFAULT_DATE_PICKER_TIME_FORMAT);
    }
    return moment(datePickerRequest).subtract(1, 'days');
  })();
  const qaRequired = watch('qaRequired', projectDetail.qa_required);
  const onSubmit = async data => {
    const tagIds = tagsRef.current?.getTagIds();
    const proposalFieldsData = proposalFieldsRef.current?.getProposalFields();
    data['flags'] = {
      is_excluded_from_stats: isExcludedFromStats,
      is_excluded_from_admin_dashboard: isExcludedFromAdminDashboard,
      is_excluded_from_billing: isExcludedFromBilling,
      is_excluded_from_slack_notifications: isExcludedFromSlackNotifications,
      can_be_charged: canBeCharged,
      can_be_billed: canBeBilled,
      can_be_matched: canBeMatched
    };
    if (proposalFieldsData) {
      if (!isSubproject) proposalFieldsData.parentId = null;
      await adminProjectDetailStore.submitEditProjectDetail({
        ...data,
        tagIds,
        ...proposalFieldsData
      });
      if (!adminProjectDetailStore.isUpdateSuccess) {
        message.error('Could not update the project because of an internal server error');
        return onCancel();
      }
      Router.reload();
    }
  };
  return <Form name="basic" layout="vertical" size="large" onFinish={handleSubmit(onSubmit)} className="edit-project-detail-form">
      <Form.Item label={<>
            Project Name
            <FontAwesomeIcon icon={faAsterisk} className="required-asterisk" />
          </>} name="projectName" initialValue={projectDetail.name}>
        <Controller name="projectName" control={control} defaultValue={projectDetail.name} rules={{
        required: true,
        maxLength: 150
      }} render={({
        field: {
          onChange,
          value
        }
      }) => {
        return <Input placeholder="Project Name" value={value} onChange={onChange} />;
      }} />
        {errors?.projectName && errors.projectName.type === ValidateError.REQUIRED && <S.Error>Please input project name</S.Error>}
        {errors?.projectName && errors.projectName.type === ValidateError.MAX_LENGTH && <S.Error>Name length must not exceed 150 characters</S.Error>}
      </Form.Item>
      <Form.Item label={<>
            Description
            <FontAwesomeIcon icon={faAsterisk} className="required-asterisk" />
          </>} name="projectDescription" initialValue={projectDetail.description}>
        <Controller name="projectDescription" defaultValue={projectDetail.description} control={control} render={({
        field: {
          onChange,
          value
        }
      }) => <Input.TextArea placeholder="Description" value={value} onChange={onChange} autoSize={{
        minRows: 5
      }} />} />
        {errors?.projectDescription && errors.projectDescription.type === ValidateError.REQUIRED && <S.Error>Please input project description</S.Error>}
      </Form.Item>
      <Form.Item label={<>
            Additional Info
            <FontAwesomeIcon icon={faAsterisk} className="required-asterisk" />
          </>} name="additionalInfo" initialValue={projectDetail.additional_info}>
        <Controller name="additionalInfo" defaultValue={projectDetail.additional_info} control={control} render={({
        field: {
          onChange,
          value
        }
      }) => <Input.TextArea placeholder="Additional Info" value={value} onChange={onChange} autoSize={{
        minRows: 5
      }} />} />
        {errors?.additionalInfo && errors.additionalInfo.type === ValidateError.REQUIRED && <S.Error>Please input additional info</S.Error>}
      </Form.Item>
      <Form.Item label="Price Breakdown" name="priceBreakdown" initialValue={projectDetail.price_breakdown}>
        <Controller name="priceBreakdown" defaultValue={projectDetail.price_breakdown} control={control} render={({
        field: {
          onChange,
          value
        }
      }) => <Input.TextArea placeholder="Price Breakdown" value={value} onChange={onChange} autoSize={{
        minRows: 1
      }} />} />
      </Form.Item>
      <Form.Item label="Delay/Hassle Comments" name="hassleComments" initialValue={projectDetail.high_level_comments}>
        <Controller name="hassleComments" defaultValue={projectDetail.high_level_comments} control={control} render={({
        field: {
          onChange,
          value
        }
      }) => <Input.TextArea placeholder="Use this field if the project has been delayed or had hassle to ensure it is included in stats." value={value} onChange={onChange} autoSize={{
        minRows: 5
      }} />} />
      </Form.Item>
      <TagsForm ref={tagsRef} defaultValue={Array.isArray(projectDetail?.tags) ? projectDetail?.tags.map(tag => tag?.id) : []} />
      <Row gutter={[24, 0]}>
        <Col span={12}>
          <Form.Item label="Project Type" name="projectType" initialValue={projectDetail.project_type || projectTypes[0]?.id}>
            <Controller name="projectType" defaultValue={projectDetail.project_type || projectTypes[0]?.id} control={control} rules={{
            required: true
          }} render={({
            field: {
              onChange,
              value
            }
          }) => <ProjectTypeSelect onChange={onChange} value={value} />} />
            {errors?.projectType && errors.projectType.type === ValidateError.REQUIRED && <S.Error>Please input your Project Type!</S.Error>}
          </Form.Item>
        </Col>
        <ProposalFields ref={proposalFieldsRef} />
        <Col span={12}>
          <Form.Item name="requestAnonymity" initialValue={projectDetail.request_anonymity}>
            <Controller name="requestAnonymity" defaultValue={projectDetail.request_anonymity} control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <RequestAnonymityCheckbox isChecked={value} onChange={onChange} />} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col span={12}>
          <Form.Item name="requestDate" label="Requested Completion Date" initialValue={moment(datePickerRequest)}>
            <Controller name="requestDate" defaultValue={moment(datePickerRequest)} control={control} rules={{
            required: true
          }} render={({
            field: {
              onChange,
              value
            }
          }) => <DatePicker disabledDate={value => value < moment()} format={DEFAULT_TIME_FORMAT} value={value} onChange={onChange} />} />
            {errors?.requestDate && errors.requestDate.type === ValidateError.REQUIRED && <S.Error>Please input date</S.Error>}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item className="form" label="CX Lead" initialValue={projectDetail.cx_lead_id}>
            <Controller name="cxLeadId" control={control} defaultValue={projectDetail.cx_lead_id} render={({
            field: {
              onChange,
              value
            }
          }) => {
            return <Select className={styles.mediumInput} options={adminOpts} size="large" value={value} onChange={onChange} />;
          }} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <Form.Item name="qaRequired" initialValue={projectDetail.qa_required}>
            <Controller name="qaRequired" control={control} defaultValue={projectDetail.qa_required} render={({
            field: {
              onChange,
              value
            }
          }) => <QARequiredCheckbox isChecked={value} onChange={onChange} />} />
          </Form.Item>
          <Form.Item initialValue={moment(datePickerQaDueDate)} name="npCompletionDate" label="NP Completion Date">
            <Controller name="npCompletionDate" control={control} defaultValue={moment(datePickerQaDueDate)} render={({
            field: {
              onChange,
              value
            }
          }) => <DatePicker allowClear={false} format={DEFAULT_TIME_FORMAT} disabled={!qaRequired} defaultValue={value} onChange={onChange} />} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <div className={styles.isSubprojectCheckboxWrapper}>
            <Checkbox checked={isSubproject} onChange={() => setIsSubProject(!isSubproject)}>
              Is Subproject
            </Checkbox>
          </div>
          <Form.Item initialValue={projectDetail.parent_id} name="parentId" label="Parent project" style={isSubproject ? {} : {
          display: 'none'
        }}>
            <Controller name="parentId" control={control} defaultValue={projectDetail.parent_id} render={({
            field: {
              onChange,
              value
            }
          }) => {
            return <S.ParentIdSelect filterOption={(input, option) => {
              const search = input.trim().toLowerCase();
              return option.label.toLowerCase().indexOf(search) >= 0;
            }} onChange={onChange} options={parentOpts} placeholder="Search projects" showSearch size="large" value={value} />;
          }} />
          </Form.Item>
        </Col>
      </Row>

      {projectDetail.flags ? <Row>
          <Col span={12}>
            <div>Include Project in...</div>
            <Form.Item name="isExcludedFromStats" initialValue={projectDetail.flags.is_excluded_from_stats}>
              <Controller name="isExcludedFromStats" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={!isExcludedFromStats} onChange={() => setIsExcludedFromStats(!isExcludedFromStats)}>
                    Stats
                  </Checkbox>} />
            </Form.Item>

            <Form.Item name="isExcludedFromAdminDashboard" initialValue={projectDetail.flags.is_excluded_from_admin_dashboard}>
              <Controller name="isExcludedFromAdminDashboard" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={!isExcludedFromAdminDashboard} onChange={() => setIsExcludedFromAdminDashboard(!isExcludedFromAdminDashboard)}>
                    Admin Dashboard
                  </Checkbox>} />
            </Form.Item>

            <Form.Item name="isExcludedFromBilling" initialValue={projectDetail.flags.is_excluded_from_billing}>
              <Controller name="isExcludedFromBilling" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={!isExcludedFromBilling} onChange={() => setIsExcludedFromBilling(!isExcludedFromBilling)}>
                    Billing
                  </Checkbox>} />
            </Form.Item>

            <Form.Item name="isExcludedFromSlackNotifications" initialValue={projectDetail.flags.is_excluded_from_slack_notifications}>
              <Controller name="isExcludedFromSlackNotifications" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={!isExcludedFromSlackNotifications} onChange={() => setIsExcludedFromSlackNotifications(!isExcludedFromSlackNotifications)}>
                    Slack Notifications
                  </Checkbox>} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <div>Project can be...</div>
            <Form.Item name="canBeBilled" initialValue={projectDetail.flags.can_be_billed}>
              <Controller name="canBeBilled" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={canBeBilled} onChange={() => setCanBeBilled(!canBeBilled)}>
                    Billed
                  </Checkbox>} />
            </Form.Item>

            <Form.Item name="canBeCharged" initialValue={projectDetail.flags.can_be_charged}>
              <Controller name="canBeCharged" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={canBeCharged} onChange={() => setCanBeCharged(!canBeCharged)}>
                    Charged
                  </Checkbox>} />
            </Form.Item>

            <Form.Item name="canBeMatched" initialValue={projectDetail.flags.can_be_matched}>
              <Controller name="canBeMatched" control={control} render={({
            field: {
              onChange,
              value
            }
          }) => <Checkbox checked={canBeMatched} onChange={() => setCanBeMatched(!canBeMatched)}>
                    Matched
                  </Checkbox>} />
            </Form.Item>
          </Col>
        </Row> : null}

      <Row>
        <Col>
          <Form.Item>
            <Button data-public className={cx(styles.updateButton, 'btn-save')} htmlType="submit" type="primary">
              {!isLoading ? 'UPDATE PROJECT' : <ClipLoader size={14} color="#FFFFFF" />}
            </Button>
          </Form.Item>
        </Col>
        <Col>
          <Form.Item>
            <Button data-public type="text" className={cx('btn-cancel', styles.cancelButton)} onClick={onCancel}>
              CANCEL
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>;
};
export default observer(EditProjectDetailForm);